import { take } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { AuthService, AuthServiceResult } from '@app/services';
import { PopupRef, PopupService } from '@progress/kendo-angular-popup';
import { MaskActionComponent } from './components/mask-action/mask-action.component';
import { getServerConfig } from '../origami-api/server-config';
import { OnRequestResult, ServiceBase } from '../origami-api/services/base/service.base';
import { ErrorCode, OrigamiUrls } from '../origami-api/constants';
import { Subscription, throwError, timer, zip } from 'rxjs';
import { NotificationsService } from '@app/services/notifications/notifications.service';
import { BuildProps } from '@app/origami-api/build-props';
import { AppService } from '@app/app.service';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { HttpClient } from '@angular/common/http';
import { LocalStorageKeys } from '@app/services/local-storage.service';
import { Router } from '@angular/router';

@Injectable()
export class SessionMonitorService implements OnDestroy {
  private strings = tiStrings.session.sessionMonitor;
  private monitor;
  private popUpRef: PopupRef;
  private subs = new Subscription();

  constructor(
    private authService: AuthService,
    private popupService: PopupService,
    private notificationsService: NotificationsService,
    private appService: AppService,
    private http: HttpClient,
    private router: Router,
  ) {
    this.subs.add(ServiceBase.onRequest.subscribe((res: OnRequestResult) => this.onRequest(res)));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      this.checkSession(BuildProps.OrigamiBuildNumber);
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.stop();
  }

  stop() {
    clearTimeout(this.monitor);
    if (this.popUpRef) {
      this.popUpRef.close();
      this.popUpRef = null;
    }
  }

  start() {
    this.stop();

    const sessionPingTimeoutMs = (getServerConfig().serverCfg.sessionPingTimeout || 10) * 60 * 1000;
    const delta = 1000;

    // TODO: below written as an old UI, but need investigate memory leak
    const pingTask = () => {
      const timeAfterLastApiCall = Date.now() - (ServiceBase.lastRequestTime || 0);
      let timeout: number;

      if (
        Math.abs(timeAfterLastApiCall - sessionPingTimeoutMs) <= delta ||
        timeAfterLastApiCall >= sessionPingTimeoutMs
      ) {
        // session renewal
        this.checkSession(BuildProps.OrigamiBuildNumber);
        timeout = sessionPingTimeoutMs;
      } else {
        // session is alive
        timeout = sessionPingTimeoutMs - timeAfterLastApiCall;
      }

      this.monitor = setTimeout(pingTask, timeout);
    };

    this.monitor = setTimeout(pingTask, sessionPingTimeoutMs);
  }

  private checkSession(buildVersion: string) {
    // we need to check both build version and hotfix index
    zip(
      this.authService.checkSession(true).pipe(take(1)),
      this.http.get<BuildProps>(`assets/build-props.json?nocache=${Date.now()}`).pipe(take(1)),
    ).subscribe(([authInfo, buildProps]) => {
      BuildProps.init(buildProps);

      this.checkVersionAndSession(buildVersion, BuildProps.HotfixIndex, authInfo);
    });
  }

  private checkVersionAndSession(buildVersion: string, hotfixIndex: number, authInfo: AuthServiceResult) {
    if (!authInfo.hasSession) {
      return this.logout(() => this.router.navigate(['/auth/login']));
      //return this.logout(() => this.authService.logout(LogoffType.AutoLogOffByTimeout));
    }

    const buildVersionChanged =
      buildVersion &&
      authInfo.info.BuildVersion !== '1.0.0.0' && // this is for BE devs
      buildVersion !== authInfo.info.BuildVersion;

    if (buildVersionChanged) {
      localStorage.removeItem(LocalStorageKeys.ti_hotfix_index);
    }

    let existingHotfixIndex;

    if (
      localStorage.getItem(LocalStorageKeys.ti_hotfix_index) === null ||
      localStorage.getItem(LocalStorageKeys.ti_hotfix_index) === undefined
    ) {
      existingHotfixIndex = hotfixIndex;
      localStorage.setItem(LocalStorageKeys.ti_hotfix_index, `${hotfixIndex}`);
    } else {
      existingHotfixIndex = +localStorage.getItem(LocalStorageKeys.ti_hotfix_index);
    }

    if (buildVersionChanged || hotfixIndex !== existingHotfixIndex) {
      this.popUpRef = this.popupService.open({
        animate: false,
        content: MaskActionComponent,
      });

      const instance: MaskActionComponent = this.popUpRef.content.instance;
      instance.secondsToAction = 15;
      instance.mainMessage = (seconds) => tiStringsFormat(this.strings.secondMessagePattern, `${seconds}`);
      localStorage.setItem(LocalStorageKeys.ti_hotfix_index, `${hotfixIndex}`);
      this.subs.add(
        instance.done.subscribe(() => {
          this.appService.removeBeforeUnloadListener();
          window.location.reload();
        }),
      );
      instance.start();

      clearInterval(this.monitor);
    }
  }

  private onRequest(res: OnRequestResult) {
    if (typeof res.error === 'string') {
      // we've got "Location" header
      this.appService.removeBeforeUnloadListener();

      window.location.href = res.error.replace(
        'clientdata',
        encodeURIComponent(encodeURIComponent(window.location.hash /*.replace(/#/g, '')*/)).toLowerCase(),
      );
      return;
    }

    const noAuthErrorCodes: ErrorCode[] = [
      ErrorCode.SESSION_EXPIRED,
      ErrorCode.SESSION_EXPIRED2,
      ErrorCode.PARALLEL_LOGIN,
      ErrorCode.CAS_SIGNOUT,
      ErrorCode.TIMEOUT_SIGNOUT,
      ErrorCode.SUPERADMIN_SIGNOUT,
    ];

    if (noAuthErrorCodes.indexOf(res.error) !== -1) {
      this.logout(() => {
        // time for showWarning from code below
        timer(1000).subscribe(() => this.authService.logout());
      });
    }

    if (res.error === ErrorCode.MAINTENANCE_IN_PROGRESS) {
      window.location.href = OrigamiUrls.maintenanceInProgressPage;
      return;
    }

    if (res.error < 0) {
      // HTTP errors
      this.notificationsService.showError('Network Error', null, null, true);
      throwError(res.response);
      return;
    }

    if (res.error) {
      this.notificationsService.showWarning(res.error, null, null, true);
      throwError(res.response);
    }
  }

  private logout(logout: () => void) {
    const isCAS =
      getServerConfig().serverCfg.centralAuthentication === 'cas'; /* && location.href.indexOf('localhost') === -1*/

    if (isCAS) {
      this.appService.removeBeforeUnloadListener();
    }

    logout();
  }
}
